import React from 'react';
import classNames from 'classnames';
import { css } from '@emotion/react';
import { caption_arrow_button_css, caption_text_button_css } from './styles';
import { buildCSSWithLength } from '../../styles';
import { IconArrowChevronDoubleLeftBold, IconArrowChevronDoubleRightBold } from '../icons/generated';
import { useDatePickerAttributes } from './hooks/useDatePickerAttributes';
import { captionColor } from './Caption';
import { Stack } from '../stack';
import { TextButton } from '../button';
import { text_styles } from '../../foundation';
import { DisplayMode } from './types';

export interface YearMonthCaptionProps {
  className?: string;
  displayMode: DisplayMode;
  label: string;
  disabledLabel?: boolean;
  /**
   * 이전 버튼 비활성화 여부
   *
   * true: 이전 년도/범위로 이동하는 버튼이 비활성화됩니다
   * false: 이전 버튼 클릭 가능
   *
   * @default undefined
   */
  disabledPrev?: boolean;
  /**
   * 다음 버튼 비활성화 여부
   *
   * true: 다음 년도/범위로 이동하는 버튼이 비활성화됩니다
   * false: 다음 버튼 클릭 가능
   *
   * @default undefined
   */
  disabledNext?: boolean;
  onClickLabel?: () => void;
  /**
   * 이전 버튼 클릭 시 호출되는 콜백
   *
   * 이전 년도 또는 년도 범위로 이동하는 동작을 수행합니다.
   *
   * @default undefined
   */
  onPrev?: () => void;
  /**
   * 다음 버튼 클릭 시 호출되는 콜백
   *
   * 다음 년도 또는 년도 범위로 이동하는 동작을 수행합니다.
   *
   * @default undefined
   */
  onNext?: () => void;
}

export const YearMonthCaption = ({
  className,
  displayMode,
  label,
  disabledLabel = false,
  disabledPrev,
  disabledNext,
  onClickLabel,
  onPrev,
  onNext,
}: YearMonthCaptionProps) => {
  const {
    caption: {
      kind,
      arrow: { size: arrowSize, width: arrowWidth, height: arrowHeight },
    },
  } = useDatePickerAttributes(displayMode);

  const arrowButtonStyles = [
    caption_arrow_button_css,
    css`
      ${buildCSSWithLength('height', arrowHeight)};
      ${buildCSSWithLength('width', arrowWidth)};
      ${buildCSSWithLength('line-height', arrowHeight / 2)};
    `,
  ];

  const textButtonStyles = [
    caption_text_button_css,
    css`
      ${text_styles[kind]}
    `,
  ];

  return (
    <Stack
      direction='row'
      width='100%'
      className={classNames('pds-year-month-caption', className)}
      justify='space-between'
    >
      <button
        type='button'
        disabled={disabledPrev}
        css={arrowButtonStyles}
        className={classNames('previous-button', { 'prev-multiple-months': false })}
        onClick={onPrev}
      >
        <IconArrowChevronDoubleLeftBold size={arrowSize} color='currentColor' />
      </button>
      <TextButton css={textButtonStyles} disabled={disabledLabel} color={captionColor(false)} onClick={onClickLabel}>
        {label}
      </TextButton>
      <button
        type='button'
        disabled={disabledNext}
        css={arrowButtonStyles}
        className={classNames('next-button', { 'next-multiple-months': false })}
        onClick={onNext}
      >
        <IconArrowChevronDoubleRightBold size={arrowSize} color='currentColor' />
      </button>
    </Stack>
  );
};
